import { clone } from "ramda";
import actionCreatorFactory from "typescript-fsa";
import { asyncFactory, thunkToAction } from "typescript-fsa-redux-thunk";

import jwtDecode from "jwt-decode";
import { isDbsJwtLoginEnabled } from "src/configState/selector";
import { INCORRECT_LOGIN } from "src/constants";
import api from "../api";
import {
  getTokenType,
  saveRedirectUrl,
  setUrlToRedirectAfterLogout
} from "../route/LocalStorage";
import { State } from "../state";
import {
  DecodedIdToken,
  LoginSuccessParams,
  NotLoggedInUser,
  ResetPasswordSuccessParams,
  User
} from "./types";

const userSyncActionCreator = actionCreatorFactory();

const userAsyncActionCreator = actionCreatorFactory("User");
const userAsyncFactory = asyncFactory<State>(userAsyncActionCreator);

export const ErrorLoggedOutUser = (errorMessage: string = INCORRECT_LOGIN) => {
  const notLoggedInUser = clone(NotLoggedInUser);
  notLoggedInUser.errorMessage = errorMessage;
  return notLoggedInUser;
};

export interface LoginCredentialParams {
  username: string;
  password: string;
  isDbsJwtLogin: boolean;
  authType?: string;
}

export const resetUser = userSyncActionCreator<{ errorMessage: string }>(
  "RESET_USER"
);

export const setUserSSO = userSyncActionCreator<{
  issuedAt: number;
  expiresIn: number;
  access_token: string;
  userId: string;
}>("SET_USER");

export const updateLastUsed = userSyncActionCreator<{ lastUsed: string }>(
  "UPDATE_LAST_USED"
);

export const storeLocalStorageUser = userSyncActionCreator(
  "STORE_LS_USER_TO_REDUX"
);

export const login = userAsyncFactory<LoginCredentialParams, User, User>(
  "LOAD",
  async (params, dispatch, getState): Promise<User> => {
    try {
      let loginSuccessParams: LoginSuccessParams;
      if (isDbsJwtLoginEnabled(getState()) && params.isDbsJwtLogin) {
        loginSuccessParams = await api.login.dbsJwtPost(params.username);
      } else {
        loginSuccessParams = await api.login.post(
          params.username,
          params.password
        );
      }
      const APICredentials = Object.assign(loginSuccessParams);
      APICredentials.lastUsed = APICredentials.issuedAt;

      const decodedToken = jwtDecode(
        loginSuccessParams.idToken
      ) as DecodedIdToken;

      const qaTeams = decodedToken.qaTeams
        ? JSON.parse(decodedToken.qaTeams)
        : [];

      setUrlToRedirectAfterLogout("/login");

      return {
        username: params.username,
        credentials: APICredentials,
        isAuthenticated: true,
        errorMessage: "",
        isBankAnalyst: decodedToken.clientUser === "true",
        hasManagedService: decodedToken.reviewSpreadContract === "true",
        userId: decodedToken.email,
        qaTeams
      };
    } catch (error) {
      throw ErrorLoggedOutUser();
    }
  }
);

const loginAction = thunkToAction(login.action);
export type LoginActionType = typeof loginAction;

export const changeUserStateSSO = userAsyncFactory(
  "CHANGE_USER_STATE_SSO",
  async (params, dispatch, getState) => {
    dispatch(setUserSSO);
    // saveRedirectUrl("");
  }
);

const changeUserStateSSOAction = thunkToAction(changeUserStateSSO.action);
export type ChangeUserStateType = typeof changeUserStateSSOAction;

export const expireOktaSession = userAsyncFactory(
  "EXPIRE_OKTA_SESSION",
  async (params, dispatch, getState) => {
    try {
      const signoutUrl = getState().config.ssoSignoutUrl;

      const url = await api.expireSession.okta(signoutUrl);
      // tslint:disable-next-line: no-console
      console.log("url", url);

      dispatch({
        payload: {},
        type: "RESET_USER"
      });
      location.href = url;

      return NotLoggedInUser;
    } catch (error) {
      return ErrorLoggedOutUser();
    }
  }
);
const expireOktaSessionAction = thunkToAction(expireOktaSession.action);
export type ExpireOktaSessionActionType = typeof expireOktaSessionAction;

export const expireSession = userAsyncFactory<User, User, User>(
  "EXPIRE_SESSION",
  async (params, dispatch, getState) => {
    const refreshToken = getState().user.user.credentials.refreshToken;
    const accessToken = getState().user.user.credentials.accessToken;
    const authType = getTokenType() === "jwt" ? "sso" : "native";
    try {
      const expireSessionParams = await api.expireSession.post(
        accessToken,
        refreshToken,
        authType
      );
      saveRedirectUrl("");
      return expireSessionParams.expire
        ? NotLoggedInUser
        : ErrorLoggedOutUser(expireSessionParams.error);
    } catch (error) {
      return ErrorLoggedOutUser();
    }
  }
);
const expireSessionAction = thunkToAction(expireSession.action);
export type ExpireSessionActionType = typeof expireSessionAction;

export interface ResetPasswordParams {
  username: string;
  password: string;
  confirmationCode: string;
}

export const resetPassword = userAsyncFactory<
  ResetPasswordParams,
  ResetPasswordSuccessParams
>(
  "RESET_PASSWORD",
  async (params, dispatch, getState): Promise<ResetPasswordSuccessParams> => {
    try {
      return await api.resetUserPassword.post(
        params.username,
        params.password,
        params.confirmationCode
      );
    } catch (error) {
      return { passwordChanged: false, errorText: error.toString() };
    }
  }
);

const resetPasswordAction = thunkToAction(resetPassword.action);
export type ResetPasswordActionType = typeof resetPasswordAction;

export const getSSOCredentials = userAsyncFactory(
  "GET_SSO_CREDENTIALS",
  async (params, dispatch, getState): Promise<string> => {
    try {
      return await api.login.getSSOCredentials();
    } catch (error) {
      return "Failed to get SSO credentials";
    }
  }
);

const getSSOCredentialsAction = thunkToAction(getSSOCredentials.action);
export type GetSSOCredentialsActionType = typeof getSSOCredentialsAction;



// WEBPACK FOOTER //
// ./src/login/actions.ts